/**
 * WorkshopList Component - Displays list of user's workshops
 * 
 * Part of the modular Workshop Builder architecture.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';
import { getStatusBadge } from './WorkshopUtils';
import { Pagination } from '@/components/Pagination';

export const WorkshopList = ({ 
  workshops, 
  selectedWorkshop, 
  onSelectWorkshop,
  userId 
}) => {
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  // Filter out pending invitations for display in main list
  const displayWorkshops = workshops.filter(
    w => !w.collaborators?.some(c => c.user_id === userId && c.status === 'pending')
  );
  const totalPages = Math.max(1, Math.ceil(displayWorkshops.length / PER_PAGE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);
  const pagedWorkshops = displayWorkshops.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  
  return (
    <Card className="bg-slate-800">
      <CardHeader>
        <CardTitle className="text-lg text-white">My Workshops</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
        {displayWorkshops.length === 0 ? (
          <p className="text-gray-300 text-center py-8">
            No workshops yet. Create your first one!
          </p>
        ) : (
          <>
            {pagedWorkshops.map(workshop => (
              <div 
                key={workshop.id}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedWorkshop?.id === workshop.id 
                    ? 'bg-cyan-600/20 border border-cyan-500' 
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
                onClick={() => onSelectWorkshop(workshop.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-white">{workshop.title}</p>
                    <p className="text-sm text-gray-300">${workshop.price.toFixed(2)}</p>
                  </div>
                  {getStatusBadge(workshop.status)}
                </div>
                {workshop.collaborators?.length > 1 && (
                  <div className="flex items-center gap-1 mt-2">
                    <Users className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-400">
                      {workshop.collaborators.length} collaborators
                    </span>
                  </div>
                )}
              </div>
            ))}
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              testidPrefix="workshop-list-pagination"
              theme="dark"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
